import { Icon, IssueIcon, Pane } from 'evergreen-ui';
import { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';

import { getTrackBackground, Range } from 'react-range';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-query';
import { updateSettings } from '../../../queries/settings';
import { setSettings } from '../../../store/settingsSlice';

const StyledSelector = styled.div`
  padding: 15px 25px;
  display: flex;
  flex-direction: column;

  .timeSelector {
    display: flex;
    flex: 1;

    .select {
      display: flex;
      flex-direction: column;
      color: #a7aaac;
      font-size: 14px;
      font-weight: 500;
      flex: 1;

      .timeContainer {
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 8px;
        background: #2b2d2e;
        color: #ffffff;
        height: 42px;
        font-size: 16px;
        padding: 5px 20px;
        min-width: 120px;
        justify-content: flex-start;

        & > span {
          color: #a7aaac;
          font-weight: 400;
          margin-left: 10px;
        }
      }
    }
  }

  .hourbars {
    display: flex;
    align-items: flex-end;
    margin: 45px 0 0;
    height: 100px;

    .hourbar {
      display: flex;
      flex: 1;
      background: #e78850;
      margin: 2px;
      border-radius: 2px 2px 0 0;

      &.disabled {
        background: #3e4143;
      }
    }
  }

  .hourlabels {
    display: flex;
    justify-content: space-between;

    p {
      font-size: 12px;
      font-weight: 500;
      color: #65696c;
    }
  }

  .tip {
    display: flex;
    color: #a7aaac;
    margin: 45px 0 15px;

    p {
      margin: 0;
      margin-left: 15px;
      font-size: 13px;
    }
  }
`;

const hoursAndMinutes = [
  {
    label: '12:00',
    subLabel: 'AM',
    value: '00:00',
    height: Math.floor(10 + Math.random() * 9000) / 100 + '%',
  },
  {
    label: '12:30',
    subLabel: 'AM',
    value: '00:30',
    height: Math.floor(10 + Math.random() * 9000) / 100 + '%',
  },
  ...new Array(46).fill(1).map((_, t) => ({
    label: `${((_ = Math.floor(((t += 2) * 30) / 60)) < 13 ? _ : _ % 12).toString().padStart(2, '0')}:${((t * 30) % 60)
      .toString()
      .padStart(2, '0')}`,
    subLabel: _ < 12 ? 'AM' : 'PM',
    value: `${_.toString().padStart(2, '0')}:${((t * 30) % 60).toString().padStart(2, '0')}`,
    height: Math.floor(10 + Math.random() * 9000) / 100 + '%',
  })),
  {
    label: '12:00',
    subLabel: 'PM',
    value: '00:00',
    height: Math.floor(10 + Math.random() * 9000) / 100 + '%',
  },
];

const bars = [
  {
    label: '12:00',
    subLabel: 'AM',
    value: '00:00',
    height: Math.floor(10 + Math.random() * 9000) / 100 + '%',
  },
  {
    label: '12:30',
    subLabel: 'AM',
    value: '00:30',
    height: Math.floor(10 + Math.random() * 9000) / 100 + '%',
  },
  ...new Array(22).fill(1).map((_, t) => ({
    label: `${((_ = Math.floor(((t += 2) * 30) / 60)) < 13 ? _ : _ % 12).toString().padStart(2, '0')}:${((t * 30) % 60)
      .toString()
      .padStart(2, '0')}`,
    subLabel: _ < 12 ? 'AM' : 'PM',
    value: `${_.toString().padStart(2, '0')}:${((t * 30) % 60).toString().padStart(2, '0')}`,
    height: Math.floor(10 + Math.random() * 9000) / 100 + '%',
  })),
];

const MIN = 0;
const MAX = 48;

const PanelWorkingHours = ({ settings, enabled }) => {
  const dispatch = useDispatch();
  const { dayFilters } = settings;
  const [loading, setLoading] = useState(false);
  const token = useSelector(state => state.authentication.token);

  const updateSettingsQuery = useMutation(updateSettings, {
    useErrorBoundary: true,
  });
  const updateDayFilters = async df => {
    await updateSettingsQuery.mutateAsync({
      dayFilters: df,
      enabled,
      token,
    });
  };

  const [dfs, setDfs] = useState(
    dayFilters.map(({ day, start, end, rejectAll }) => ({
      day,
      start,
      end,
      rejectAll,
    })),
  );
  const getMinMax = useCallback(() => {
    if (dayFilters.length > 0) {
      let min = [...dayFilters].sort((a, b) => a.start - b.start)[0].start;
      let max = [...dayFilters].sort((a, b) => b.end - a.end)[0].end;
      let minIndex = hoursAndMinutes.findIndex(ham => `${ham.value}:00` === min);
      if (minIndex === -1) {
        const [hh, mm] = min.split(':');
        const newMm = `${Math.floor(+mm / 30) * 30}`.padStart(2, '0');
        minIndex = hoursAndMinutes.findIndex(ham => `${ham.value}` === `${hh}:${newMm}`);
      }
      let maxIndex =
        max === '00:00:00' ? hoursAndMinutes.length - 1 : hoursAndMinutes.findIndex(ham => `${ham.value}:00` === max);
      if (maxIndex === -1) maxIndex = hoursAndMinutes.length - 1;
      return [minIndex, maxIndex].sort((a, b) => a - b);
    } else {
      return [0, hoursAndMinutes.length - 1];
    }
  }, [dayFilters]);
  const [schedule, setSchedule] = useState(getMinMax());

  useEffect(() => {
    setDfs(
      dayFilters.map(({ day, start, end, rejectAll }) => ({
        day,
        start,
        end,
        rejectAll,
      })),
    );
    setSchedule(getMinMax());
  }, [dayFilters, getMinMax]);
  const updateSchedule = values => {
    if (values[1] > values[0]) setSchedule(values);
  };
  const setNewSchedule = async values => {
    setLoading(true);
    var updatedDayFilters = [];
    var updatedDayFiltersForSettings = [];
    dfs.forEach(({ day, rejectAll }) => {
      let start = `${hoursAndMinutes[values[0]].value}:00`;
      let end = `${hoursAndMinutes[values[1]].value}:00`;
      updatedDayFiltersForSettings.push({ day, start, end, rejectAll });
      start += '.000';
      end += '.000';
      updatedDayFilters.push({ day, start, end, rejectAll });
    });
    dispatch(
      setSettings({
        ...settings,
        dayFilters: updatedDayFiltersForSettings,
      }),
    );
    setSchedule(values);
    setDfs(updatedDayFilters);
    await updateDayFilters(updatedDayFilters);
    setLoading(false);
  };

  const start = hoursAndMinutes[schedule[0]];
  const end = hoursAndMinutes[schedule[1]];
  return (
    <Pane
      background="#1F2021"
      position="relative"
      display="flex"
      flex={1}
      height="100%"
      flexDirection="column"
      paddingBottom={30}
    >
      <StyledSelector>
        <h2 style={{ color: 'white', fontSize: 18, marginBottom: 0 }}>Select working hours</h2>
        <p style={{ color: '#A7AAAC' }}>
          Select between which hours you would like to accept shifts for during the day
        </p>

        <div style={{ display: 'flex' }}>
          <div className="timeSelector">
            <div className="select" style={{ marginRight: 10 }}>
              <p>Start Work Day</p>
              <div className="timeContainer">
                {start.label}
                <span>{start.subLabel}</span>
              </div>
            </div>
            <div className="select" style={{ marginLeft: 10 }}>
              <p>End Work Day</p>
              <div className="timeContainer">
                {end.label}
                <span>{end.subLabel}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="hourbars">
          {bars.map((h, key) => (
            <div
              key={key}
              className={`hourbar ${key < schedule[0] / 2 || key * 2 > schedule[1] ? 'disabled' : ''}`}
              style={{ height: h.height }}
            />
          ))}
        </div>
        <div className="hourlabels">
          <p>12AM</p>
          <p>8AM</p>
          <p>4PM</p>
          <p>12PM</p>
        </div>
        <div>
          <Range
            draggableTrack
            values={schedule}
            step={1}
            min={MIN}
            max={MAX}
            onChange={updateSchedule}
            onFinalChange={setNewSchedule}
            disabled={loading}
            renderTrack={({ props, children }) => (
              <div
                onMouseDown={props.onMouseDown}
                onTouchStart={props.onTouchStart}
                style={{
                  ...props.style,
                  height: '36px',
                  display: 'flex',
                  width: '100%',
                }}
              >
                <div
                  ref={props.ref}
                  style={{
                    height: '5px',
                    width: '100%',
                    borderRadius: '4px',
                    background: getTrackBackground({
                      values: schedule,
                      colors: ['#3E4143', '#E78850', '#3E4143'],
                      min: MIN,
                      max: MAX,
                    }),
                    alignSelf: 'center',
                  }}
                >
                  {children}
                </div>
              </div>
            )}
            renderThumb={({ props, isDragged }) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  height: '28px',
                  width: '28px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: 28,
                  outline: 'none',
                }}
              />
            )}
          />
        </div>
        <div className="tip">
          <Icon icon={IssueIcon} size={20} />
          <p>The graph above shows general price of the blocks being released for the stations you have selected</p>
        </div>
      </StyledSelector>
    </Pane>
  );
};

export default PanelWorkingHours;
